import React, { useEffect, useState } from "react";
import styled from "styled-components";
import BellIcon from "../../assets/home/bell.svg";
import AOS from "aos";
import NotifyModal from "../../components/notify-modal/notify-modal";
import { frontEnd } from "../../config/links";

interface ExamCardProps {
  title: string;
  icon: React.ReactNode;
  link: string;
  isSoon?: boolean;
  hasNotifyMe?: boolean;
  onNotifyClick?: () => void;
}

const ExamCard: React.FC<ExamCardProps> = ({
  title,
  icon,
  link,
  isSoon,
  hasNotifyMe,
  onNotifyClick
}) => {
  const handleCardClick = () => {
    if (hasNotifyMe && onNotifyClick) {
      onNotifyClick();
    } else if (!hasNotifyMe) {
      window.location.href = link;
    }
  };

  return (
    <ExamCardContainer onClick={handleCardClick}>
      <ExamInfo>
        {icon && <IconContainer>{icon}</IconContainer>}
        {/* <IconContainer>{icon}</IconContainer> */}
        <ExamTitle>{title}</ExamTitle>
        {hasNotifyMe ? (
          <ExamLink
            href="#"
            onClick={e => {
              e.preventDefault();
              e.stopPropagation();
              onNotifyClick && onNotifyClick();
            }}
          >
            Notify Me
            <img src={BellIcon} alt="Bell icon" width={20} height={20} />
          </ExamLink>
        ) : (
          <ExamLink
            href={link}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            Details & Prices&nbsp; →
          </ExamLink>
        )}
      </ExamInfo>
      {isSoon && <SoonBadge>Soon</SoonBadge>}
    </ExamCardContainer>
  );
};

const SelectExam: React.FC = () => {
  useEffect(() => {
    // Initialize AOS with settings to only animate once
    AOS.init({
      once: true, // This is the key setting - animations will only happen once
      mirror: false, // Don't mirror animations when scrolling back up
      duration: 800,
      offset: 100 // Controls when the animation triggers
      // easing: "ease-out"
    });

    // Refresh AOS on component mount
    AOS.refresh();
  }, []);

  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<string>("");

  const handleNotifyClick = (examTitle: string) => {
    setSelectedExam(examTitle);
    setIsNotifyModalOpen(true);
  };

  const handleCardClick = (link: string) => {
    window.location.href = link;
  };

  return (
    <ExamSection>
      <SectionTitle data-aos="fade-up">
        Select Your Exam
        <VectorImage src="/assets/home/vector.png" alt="vector" />
      </SectionTitle>
      <ExamGrid>
        <ExamCardContainer
          data-aos="zoom-in"
          data-aos-delay="100"
          data-aos-duration="800"
          data-aos-mirror="false"
          onClick={() => handleCardClick(frontEnd.gsse)}
        >
          <ExamInfo>
            <IconContainer>
              <ExamIcon1 src="/assets/home/gsse-icon.png" alt="GSSE" />
            </IconContainer>
            <ExamTitle>Generic Surgical Sciences Examination (GSSE)</ExamTitle>
            <ExamLink href="/gsse" onClick={e => e.stopPropagation()}>
              Details & Prices&nbsp; →
            </ExamLink>
          </ExamInfo>
        </ExamCardContainer>

        <ExamCardContainer
          data-aos="zoom-in"
          data-aos-delay="200"
          data-aos-duration="800"
          onClick={() => handleCardClick(frontEnd.bps)}
        >
          <ExamInfo>
            <IconContainer>
              <div
                style={{
                  width: "30px",
                  height: "17.32px",
                  backgroundColor: "red",
                  position: "relative",
                  margin: "8.66px 0"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    transform: "rotate(60deg)",
                    backgroundColor: "red"
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    transform: "rotate(-60deg)",
                    backgroundColor: "red"
                  }}
                />
              </div>
            </IconContainer>
            <ExamTitle>Basic Pathological Sciences (BPS)</ExamTitle>
            <ExamLink href="/bps" onClick={e => e.stopPropagation()}>
              Details & Prices&nbsp; →
            </ExamLink>
          </ExamInfo>
        </ExamCardContainer>
        <ExamCardContainer
          data-aos="fade"
          data-aos-delay="100"
          data-aos-duration="400"
          onClick={() => handleCardClick(frontEnd.gpepau)}
        >
          <ExamInfo id="why-getthru">
            <IconContainer>
              <ExamIcon4 src="/assets/home/gpep-au-icon.png" alt="gpep" />
            </IconContainer>
            <ExamTitle>
              Royal Australian College of General Practitioners AKT & KFP
              (RACGP)
            </ExamTitle>
            <ExamLink href={frontEnd.gpepau} onClick={e => e.stopPropagation()}>
              Details & Prices&nbsp; →
            </ExamLink>
          </ExamInfo>
        </ExamCardContainer>
        <ExamCardContainer
          data-aos="zoom-in"
          data-aos-delay="300"
          data-aos-duration="800"
          onClick={() =>
            handleNotifyClick("General Practice Education Programme (GPEP NZ)")
          }
        >
          <ExamInfo>
            <IconContainer>
              <ExamIcon3 src="/assets/home/gpep-icon.png" alt="gpep" />
            </IconContainer>
            <ExamTitle>
              General Practice Education Programme (GPEP NZ)
            </ExamTitle>
            <ExamLink
              href="#"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                handleNotifyClick(
                  "General Practice Education Programme (GPEP NZ)"
                );
              }}
            >
              Notify Me
              <img src={BellIcon} alt="Bell icon" width={20} height={20} />
            </ExamLink>
          </ExamInfo>
          <SoonBadge>Coming Soon</SoonBadge>
        </ExamCardContainer>
      </ExamGrid>

      {/* Notify Modal */}
      {isNotifyModalOpen && (
        <NotifyModal
          isOpen={isNotifyModalOpen}
          onClose={() => setIsNotifyModalOpen(false)}
          examName={selectedExam}
        />
      )}
    </ExamSection>
  );
};

const ExamSection = styled.div`
  padding: 32px 16px;
  max-width: 1440px;
  margin: 0 auto;
  width: 100%;
`;

const SectionTitle = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 32px;
  position: relative;

  @media (min-width: 768px) {
    font-size: 32px;
  }
`;

const VectorImage = styled.img.attrs({ width: 66, height: 11 })`
  width: 66px;
  height: 11px;
  margin-left: 8px;
  position: absolute;
  bottom: -10px;
  left: 130px;

  @media (min-width: 768px) {
    width: 88px;
    left: 180px;
  }
`;

const ExamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ExamCardContainer = styled.div`
  padding: 16px 24px;
  border-radius: 8px;
  border-color: rgb(215, 215, 215);
  border-width: 1px;
  border-style: solid;
  background: white;
  display: flex;
  align-items: start;
  // justify-content: start;
  gap: 16px;
  position: relative;
  height: 100%;
  cursor: pointer;
  transition: transform 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    border-color: #4628ff;
    border-width: 1px;
    transform: translateY(-3px);
  }

  @media (min-width: 768px) {
    padding: 24px 32px;
  }
`;

const ExamInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const IconContainer = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 10px;
  border: 2px solidrgb(149, 149, 149);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  @media (min-width: 768px) {
    width: 56px;
    height: 56px;
  }
`;

const ExamTitle = styled.h2`
  font-size: 20px;
  margin-bottom: 12px;
  max-width: 350px;
  font-weight: 700;

  @media (min-width: 768px) {
    max-width: 350px;
    font-size: 24px;
  }
`;

const ExamLink = styled.a`
  color: #4628ff;
  text-decoration: none;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  margin-top: auto;

  &:hover {
    text-decoration: underline;
  }
`;

const SoonBadge = styled.span`
  position: absolute;
  top: 16px;
  right: 24px;
  background: #8d899e;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 13px;
  color: #fff;
`;

const ExamIcon1 = styled.img.attrs({ width: 28, height: 46 })`
  height: auto;
  width: 24px;

  @media (min-width: 768px) {
    width: 32px;
  }
`;

const ExamIcon2 = styled.img`
  height: auto;
  width: 32px;

  @media (min-width: 768px) {
    width: 36px;
  }
`;

const ExamIcon3 = styled.img.attrs({ width: 24, height: 46 })`
  height: auto;
  width: 20px;
  margin-left: 4px;

  @media (min-width: 768px) {
    width: 24px;
  }
`;

const ExamIcon4 = styled.img.attrs({ width: 40, height: 40 })`
  height: auto;
  width: 32px;

  @media (min-width: 768px) {
    width: 42px;
  }
`;

export default SelectExam;
